import { TopalInvoice } from './topal-xml-builder';
import { TransactionFull } from '../../models';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

export interface InvoiceGroup {
  docNum: number;
  invoice: TopalInvoice;
  transactions: TransactionFull[];
}

function groupTransactionsByEmployee(
  transactions: TransactionFull[],
): Map<string, TransactionFull[]> {
  const groups = new Map<string, TransactionFull[]>();
  for (const transaction of transactions) {
    const userId = transaction.user.id;
    const existing = groups.get(userId);
    if (existing) {
      existing.push(transaction);
    } else {
      groups.set(userId, [transaction]);
    }
  }
  return groups;
}

function buildInvoiceText(
  lastName: string,
  firstName: string,
  exportDate: Date,
): string {
  const month = format(exportDate, 'MMM', { locale: de }).replace(/\.$/, '');
  const year = format(exportDate, 'yy');
  return `Spesen ${lastName} ${firstName[0]}. ${month} ${year}`;
}

function buildFreeInvoiceNum(userId: string, exportDate: Date): string {
  const segment = userId.split('-')[0] || userId;
  return `EXP-${segment}-${format(exportDate, 'yyyyMMdd')}`;
}

export function mapTransactionsToInvoices(
  transactions: TransactionFull[],
  exportDate: Date,
  baseCurrency: string,
): InvoiceGroup[] {
  const groups = groupTransactionsByEmployee(transactions);
  const invoiceDate = format(exportDate, 'yyyy-MM-dd');
  const results: InvoiceGroup[] = [];
  let docNum = 1;

  for (const [userId, employeeTransactions] of groups) {
    const user = employeeTransactions[0]?.user;
    if (!user) continue;
    const totalAmount = Number(
      employeeTransactions
        .reduce(
          (sum, transaction) =>
            sum + (transaction.baseCurrencyAmount ?? transaction.amount),
          0,
        )
        .toFixed(2),
    );

    const segment = userId.split('-')[0] || userId;
    const freePartyNum = `EXP-${segment}`;

    // Determine FC fields for single-foreign-currency groups
    const currencies = new Set(
      employeeTransactions.map(
        (transaction) => transaction.currencyCode.toUpperCase(),
      ),
    );
    const isSingleForeignCurrency =
      currencies.size === 1 &&
      !currencies.has(baseCurrency.toUpperCase());

    let invoiceFcFields: { exchangeRate?: number; totalAmountFC?: number } = {};
    if (isSingleForeignCurrency) {
      const totalAmountFC = Number(
        employeeTransactions
          .reduce((sum, transaction) => sum + transaction.amount, 0)
          .toFixed(2),
      );
      const rate = employeeTransactions[0]?.exchangeRate;
      invoiceFcFields = {
        totalAmountFC,
        exchangeRate:
          rate ?? (totalAmountFC !== 0 ? totalAmount / totalAmountFC : undefined),
      };
    }

    const invoice: TopalInvoice = {
      docNum,
      freePartyNum,
      personRoleFID: 3,
      payTermCode: '30T',
      payMethodName: 'IBAN-CHF',
      invoiceDate,
      totalAmount,
      freeInvoiceNum: buildFreeInvoiceNum(userId, exportDate),
      text: buildInvoiceText(user.lastName, user.firstName, exportDate),
      remarks: '',
      ...invoiceFcFields,
    };

    results.push({ docNum, invoice, transactions: employeeTransactions });
    docNum++;
  }

  return results;
}
